import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { getFeedbackPublication } from '@/lib/api';
import { feedbackPublicationKey } from '@/lib/queryClient';
import type { FeedbackDraftScope } from '@/lib/types';
import { useFeedbackAuthoringScope } from '../hooks/useFeedbackAuthoringScope';

/** A current read is context, never an implicit rebase or mutation of the draft. */
export function FeedbackCurrentPublication({ scope, feedbackId, onClose }: {
  scope: FeedbackDraftScope; feedbackId: string; onClose(): void;
}) {
  const active = useFeedbackAuthoringScope(scope);
  const query = useQuery({ queryKey: feedbackPublicationKey(scope, feedbackId),
    queryFn: ({ signal }) => getFeedbackPublication(feedbackId, { scope, signal }),
    enabled: active, staleTime: 0, retry: false, refetchOnWindowFocus: false });
  const data = active && query.isFetchedAfterMount && !query.isFetching && !query.isError ? query.data : undefined;
  return <CoachModal title="Current published Feedback" onClose={onClose}
    footer={<Button variant="outline" onClick={onClose}>Back to draft</Button>} bodyClassName="space-y-4">
    {!active ? <p role="alert">Your coaching context changed. Close and open Feedback again.</p>
      : query.isError ? <div role="alert"><p>Current Feedback could not be loaded. Your draft is unchanged.</p><Button variant="outline" onClick={() => void query.refetch()}>Try again</Button></div>
        : !data ? <p role="status">Loading current Feedback…</p>
          : <><p className="text-xs text-ink-muted">Published version {data.feedback_version}</p>
            <p className="whitespace-pre-wrap break-words text-sm">{data.message}</p>
            {data.next_step && <div><h3 className="font-semibold">Next step</h3><p className="whitespace-pre-wrap break-words text-sm">{data.next_step}</p></div>}
            <p className="text-xs text-ink-muted">Reading this version does not replace your draft.</p></>}
  </CoachModal>;
}
